import * as THREE from 'three';
import { FeaturePreviewLine } from './FeaturePreviewLine';
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent';
import { buildFeatureTopologySnapshot } from '../../utils/feature/buildFeatureTopologySnapshot';

/** 从三角网格提取预览轮廓时使用的面夹角阈值。 */
const PREVIEW_MESH_OUTLINE_THRESHOLD_ANGLE = 30;

/**
 * 判断对象是否为特征预览体。
 *
 * @param value 场景对象。
 * @returns 是否为特征预览体。
 */
export function isFeaturePreviewMesh(value: THREE.Object3D): value is FeaturePreviewMesh {
  return Reflect.get(value, 'isFeaturePreviewMesh') === true;
}

/**
 * 将连续曲线采样点转换为 LineSegments 所需的点对序列。
 *
 * @param points 连续曲线采样点。
 * @returns 线段点对序列。
 */
function buildPreviewLineSegmentPoints(points: Array<[number, number, number]>): THREE.Vector3[] {
  if (points.length < 2) {
    return [];
  }
  const segmentPoints: THREE.Vector3[] = [];
  for (let index = 0; index < points.length - 1; index += 1) {
    const start = points[index]!;
    const end = points[index + 1]!;
    segmentPoints.push(
      new THREE.Vector3(start[0], start[1], start[2]),
      new THREE.Vector3(end[0], end[1], end[2]),
    );
  }
  return segmentPoints;
}

/**
 * 判断几何体是否包含可用于构建轮廓线的位置数据。
 *
 * @param geometry 预览体几何。
 * @returns 几何体可生成轮廓时返回 true。
 */
function hasRenderablePositionAttribute(geometry: THREE.BufferGeometry): boolean {
  const position = geometry.getAttribute('position');
  return Boolean(position && position.count >= 2);
}

/**
 * 特征预览体封装。
 *
 * 文件定位：
 * 1. 当前文件位于 cad-core 的 mesh/feature 目录，属于三维特征可视化对象层；
 * 2. 它负责承载预览 mesh 本体和黄色描边；
 * 3. 预览体不参与正式选择链，因此 mesh 与描边都必须禁用拾取。
 */
export class FeaturePreviewMesh extends THREE.Mesh {
  /** 运行时类型标记。 */
  public readonly isFeaturePreviewMesh: boolean = true;
  /** 当前预览描边对象集合。 */
  private readonly previewLines: FeaturePreviewLine[] = [];
  /** 预览网格面三角形范围，用于缺少权威拓扑时生成轻量轮廓。 */
  private readonly faceTriangleRanges: [number, number][] | null;

  /**
   * 构造函数。
   *
   * @param input 输入参数。
   */
  public constructor(input: {
    meshData: MeshData;
    geometry: THREE.BufferGeometry;
    material: THREE.Material | THREE.Material[];
  }) {
    super(input.geometry, input.material);
    this.name = input.meshData.id;
    this.renderOrder = 10;
    this.faceTriangleRanges = input.meshData.faceTriangleRanges
      ? input.meshData.faceTriangleRanges.map(([start, end]) => [start, end])
      : null;
  }

  /**
   * 同步预览描边。
   *
   * @param snapshot 拓扑快照。
   */
  public syncPreviewLines(snapshot: TopologySnapshot | null): void {
    this.clearPreviewLines();
    if (this.syncTopologyPreviewLines(snapshot) > 0) {
      return;
    }

    this.syncGeometryOutlinePreviewLine();
  }

  /**
   * 根据权威拓扑快照同步预览描边。
   *
   * @param snapshot 拓扑快照。
   * @returns 实际创建的预览描边数量。
   */
  private syncTopologyPreviewLines(snapshot: TopologySnapshot | null): number {
    if (!snapshot) {
      return 0;
    }

    let createdLineCount = 0;
    snapshot.edges.forEach((edge) => {
      const vertexA = snapshot.vertices[edge.vertexAIndex];
      const vertexB = snapshot.vertices[edge.vertexBIndex];
      if (!vertexA || !vertexB) {
        return;
      }

      const linePoints = edge.sampledPoints && edge.sampledPoints.length > 1
        ? edge.sampledPoints
        : [vertexA.position, vertexB.position];
      const segmentPoints = buildPreviewLineSegmentPoints(linePoints);
      if (segmentPoints.length <= 0) {
        return;
      }
      const geometry = new THREE.BufferGeometry().setFromPoints(segmentPoints);
      const previewLine = new FeaturePreviewLine(geometry);
      this.previewLines.push(previewLine);
      addObject3DToParent(previewLine, this);
      createdLineCount += 1;
    });
    return createdLineCount;
  }

  /**
   * 在缺少权威拓扑时，从当前预览三角网格提取轻量轮廓。
   *
   * 设计说明：
   * 1. 圆角、倒角和曲线 profile 预览出于性能原因可能不会携带完整 Puppy CAD 内核 拓扑快照；
   * 2. 预览体仍必须具备可见轮廓，因此这里只基于已存在的 BufferGeometry 生成显示线；
   * 3. 该 fallback 不承担选择语义，也不替代正式结果体的权威点线面拓扑。
   */
  private syncGeometryOutlinePreviewLine(): void {
    if (this.syncFaceRangePreviewLines() > 0) {
      return;
    }

    if (!hasRenderablePositionAttribute(this.geometry)) {
      return;
    }

    const outlineGeometry = new THREE.EdgesGeometry(
      this.geometry,
      PREVIEW_MESH_OUTLINE_THRESHOLD_ANGLE,
    );
    const position = outlineGeometry.getAttribute('position');
    if (!position || position.count <= 0) {
      outlineGeometry.dispose();
      return;
    }

    const previewLine = new FeaturePreviewLine(outlineGeometry);
    this.previewLines.push(previewLine);
    addObject3DToParent(previewLine, this);
  }

  /**
   * 使用面范围推导预览轮廓。
   *
   * @returns 实际创建的预览描边数量。
   */
  private syncFaceRangePreviewLines(): number {
    if (!this.faceTriangleRanges || this.faceTriangleRanges.length === 0) {
      return 0;
    }

    const fallbackSnapshot = buildFeatureTopologySnapshot(this.geometry, this.faceTriangleRanges);
    return this.syncTopologyPreviewLines(fallbackSnapshot);
  }

  /**
   * 清理预览描边。
   */
  public clearPreviewLines(): void {
    this.previewLines.forEach((previewLine) => {
      if (previewLine.parent === this) {
        this.remove(previewLine);
      }
      previewLine.geometry.dispose();
    });
    this.previewLines.length = 0;
  }

  /**
   * 释放预览体持有的附属资源。
   */
  public disposePreviewResources(): void {
    this.clearPreviewLines();
  }

  /**
   * 禁用预览体拾取。
   *
   * 设计说明：
   * 1. 预览体只负责显示，不能抢占草图和正式实体的交互；
   * 2. 覆写 raycast 可以保证拖拽和选择命中仍然回到真实可编辑对象。
   */
  public override raycast(): void {
    return;
  }
}
